// Payments/CustomerPaymentsTable.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Alert, Box, TextField, Stack, Chip,
  Typography, useTheme, alpha
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

const FONT = "'Plus Jakarta Sans', sans-serif";

const CustomerPaymentsTable = ({ perPage = 10, showPagination = true, refreshKey }) => {
  const theme = useTheme();
  const [payments, setPayments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(perPage);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    setIsLoading(true);
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
      start_date: startDate || null,
      end_date: endDate || null,
    };
    axiosInstance.get(`/payments/customer-payments`, { params })
      .then(res => {
        setPayments(res.data.data);
        setTotalRows(res.data.total);
      })
      .catch(err => setError('Failed to fetch payments.'))
      .finally(() => setIsLoading(false));
  }, [page, rowsPerPage, startDate, endDate, refreshKey]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusChipSx = (status) => {
    const s = status?.toLowerCase();
    if (s === 'succeeded' || s === 'completed') {
      return {
        bgcolor: alpha('#16a34a', 0.1),
        color: '#16a34a',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 24,
        borderRadius: 1.5,
        fontFamily: FONT,
        '& .MuiChip-label': { px: 1.2 },
      };
    }
    if (s === 'pending') {
      return {
        bgcolor: alpha('#d97706', 0.1),
        color: '#d97706',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 24,
        borderRadius: 1.5,
        fontFamily: FONT,
        '& .MuiChip-label': { px: 1.2 },
      };
    }
    if (s === 'failed') {
      return {
        bgcolor: alpha('#dc2626', 0.1),
        color: '#dc2626',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 24,
        borderRadius: 1.5,
        fontFamily: FONT,
        '& .MuiChip-label': { px: 1.2 },
      };
    }
    return {
      bgcolor: alpha(theme.palette.text.secondary, 0.08),
      color: theme.palette.text.secondary,
      fontWeight: 600,
      fontSize: '0.7rem',
      height: 24,
      borderRadius: 1.5,
      fontFamily: FONT,
      '& .MuiChip-label': { px: 1.2 },
    };
  };

  const getGatewayChipSx = (gateway) => {
    const base = {
      fontWeight: 600,
      fontSize: '0.7rem',
      height: 24,
      borderRadius: 1.5,
      fontFamily: FONT,
      '& .MuiChip-label': { px: 1.2 },
    };
    switch (gateway?.toLowerCase()) {
      case 'stripe':
        return { ...base, bgcolor: alpha('#1a73e8', 0.1), color: '#1a73e8' };
      case 'paypal':
        return { ...base, bgcolor: alpha('#003087', 0.1), color: '#003087' };
      case 'coinbase':
        return { ...base, bgcolor: alpha('#f7931a', 0.1), color: '#f7931a' };
      default:
        return { ...base, bgcolor: alpha(theme.palette.text.secondary, 0.08), color: theme.palette.text.secondary };
    }
  };

  const getGatewayLabel = (gateway) => {
    switch (gateway?.toLowerCase()) {
      case 'stripe': return 'Stripe';
      case 'paypal': return 'PayPal';
      case 'coinbase': return 'Coinbase';
      default: return gateway || '-';
    }
  };

  const getTxExplorerUrl = (txHash, network) => {
    if (!txHash || !network) return null;
    switch (network?.toLowerCase()) {
      case 'ethereum':
        return `https://etherscan.io/tx/${txHash}`;
      case 'solana':
        return `https://solscan.io/tx/${txHash}`;
      case 'bitcoin':
        return `https://www.blockchain.com/btc/tx/${txHash}`;
      default:
        return null;
    }
  };

  const truncateHash = (hash) => {
    if (!hash) return '-';
    if (hash.length <= 10) return hash;
    return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
  };

  /* ---- Shared cell styles ---- */
  const headCellSx = {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    bgcolor: alpha(theme.palette.text.primary, 0.02),
    borderBottom: `2px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
    py: 1.5,
  };

  const bodyCellSx = {
    fontFamily: FONT,
    fontSize: '0.85rem',
    py: 1.5,
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  /* ---- Loading state ---- */
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={32} thickness={4} />
      </Box>
    );
  }

  /* ---- Error state ---- */
  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ fontFamily: FONT, borderRadius: 2 }}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* ---- Filter bar ---- */}
      {showPagination && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            variant="outlined"
            sx={{
              minWidth: 180,
              '& .MuiInputBase-root': { fontFamily: FONT, fontSize: '0.85rem', borderRadius: 2 },
              '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: '0.82rem' },
            }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            variant="outlined"
            sx={{
              minWidth: 180,
              '& .MuiInputBase-root': { fontFamily: FONT, fontSize: '0.85rem', borderRadius: 2 },
              '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: '0.82rem' },
            }}
          />
        </Stack>
      )}

      {/* ---- Table ---- */}
      <TableContainer sx={{
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.divider} transparent`,
        '&::-webkit-scrollbar': { width: 6, height: 6 },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.divider,
          borderRadius: 3,
          '&:hover': { backgroundColor: theme.palette.action.disabled },
        },
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 48 }}>#</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 130 }}>Date</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 180 }}>Customer</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 100 }} align="right">Amount</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 90 }}>Gateway</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 80 }}>Token</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 130 }}>Tx Hash</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 110 }}>Purpose</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 90 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ border: 0 }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                  }}>
                    <ReceiptLongOutlinedIcon
                      sx={{
                        fontSize: 48,
                        color: alpha(theme.palette.text.secondary, 0.25),
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      No payments found
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.8rem',
                        color: alpha(theme.palette.text.secondary, 0.6),
                        mt: 0.5,
                      }}
                    >
                      Adjust the date filters or check back later.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              payments.map((row) => (
                <TableRow
                  key={row.s_no}
                  sx={{
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  {/* # */}
                  <TableCell sx={{ ...bodyCellSx, color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {row.s_no}
                  </TableCell>

                  {/* Date */}
                  <TableCell sx={bodyCellSx}>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                      {row.datetime}
                    </Typography>
                  </TableCell>

                  {/* Customer */}
                  <TableCell sx={bodyCellSx}>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.3 }}>
                      {row.customer_name}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: theme.palette.text.secondary, mt: 0.2 }}>
                      {row.customer_email}
                    </Typography>
                  </TableCell>

                  {/* Amount */}
                  <TableCell sx={bodyCellSx} align="right">
                    <Typography sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      fontFeatureSettings: '"tnum"',
                      color: theme.palette.text.primary,
                    }}>
                      ${row.amount}
                    </Typography>
                  </TableCell>

                  {/* Gateway */}
                  <TableCell sx={bodyCellSx}>
                    {row.gateway ? (
                      <Chip
                        label={getGatewayLabel(row.gateway)}
                        size="small"
                        sx={getGatewayChipSx(row.gateway)}
                      />
                    ) : (
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: theme.palette.text.disabled }}>-</Typography>
                    )}
                  </TableCell>

                  {/* Token */}
                  <TableCell sx={bodyCellSx}>
                    {row.gateway?.toLowerCase() === 'coinbase' && row.crypto_token ? (
                      <Chip
                        label={row.crypto_token}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 24,
                          borderRadius: 1.5,
                          fontFamily: FONT,
                          bgcolor: alpha(theme.palette.text.primary, 0.06),
                          color: theme.palette.text.primary,
                          '& .MuiChip-label': { px: 1.2 },
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: theme.palette.text.disabled }}>-</Typography>
                    )}
                  </TableCell>

                  {/* Tx Hash */}
                  <TableCell sx={bodyCellSx}>
                    {row.tx_hash ? (() => {
                      const url = getTxExplorerUrl(row.tx_hash, row.crypto_network);
                      return url ? (
                        <Typography
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.78rem',
                            fontWeight: 500,
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {truncateHash(row.tx_hash)}
                          <OpenInNewIcon sx={{ fontSize: 13, opacity: 0.7 }} />
                        </Typography>
                      ) : (
                        <Typography sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.78rem',
                          color: theme.palette.text.secondary,
                        }}>
                          {truncateHash(row.tx_hash)}
                        </Typography>
                      );
                    })() : (
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: theme.palette.text.disabled }}>-</Typography>
                    )}
                  </TableCell>

                  {/* Purpose */}
                  <TableCell sx={bodyCellSx}>
                    {row.purpose ? (
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: theme.palette.text.primary }}>
                        {row.purpose}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: theme.palette.text.disabled }}>-</Typography>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={bodyCellSx}>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={getStatusChipSx(row.status)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---- Pagination ---- */}
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: FONT,
              fontSize: '0.8rem',
            },
            '& .MuiTablePagination-select': {
              fontFamily: FONT,
              fontSize: '0.8rem',
            },
          }}
        />
      )}
    </Box>
  );
};

export default CustomerPaymentsTable;
